import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { sizeUnit } from "@/common/locales"
import Calc from "@/common/util/calculator"
import type Sign from "@/signomatic/app/sign"
import { type ActiveObjectInfo } from "@/signomatic/app/sign"

const format = (size: number) => {
  const us = sizeUnit === "in"
  return size.toFixed(us ? 1 : 0)
}

interface ObjectInfoProps {
  sign: Sign
  objectInfo: ActiveObjectInfo | null
}

const ObjectInfo: React.FC<ObjectInfoProps> = ({ sign, objectInfo }) => {
  const { t } = useTranslation()
  const [angle, setAngle] = useState<boolean>(false)

  const handleRotatingStart = useCallback(() => {
    if (angle) {
      return
    }

    setAngle(true)
  }, [angle])

  const handleRotatingStop = useCallback(() => {
    setAngle(false)
  }, [])

  useEffect(() => {
    sign.onRotatingStarted.subscribe(handleRotatingStart)
    sign.onRotatingStopped.subscribe(handleRotatingStop)

    return () => {
      sign.onRotatingStarted.unsubscribe(handleRotatingStart)
      sign.onRotatingStopped.unsubscribe(handleRotatingStop)
    }
  }, [handleRotatingStart, handleRotatingStop, sign.onRotatingStarted, sign.onRotatingStopped])

  if (!objectInfo) {
    return null
  }

  const us = sizeUnit === "in"
  let { width, height, left, right, top, bottom } = objectInfo

  width = Calc.pxToMm(width)
  height = Calc.pxToMm(height)

  if (us) {
    width = Calc.mmToIn(width)
    height = Calc.mmToIn(height)
  }

  left = Calc.pxToMm(left)
  right = Calc.pxToMm(right)
  top = Calc.pxToMm(top)
  bottom = Calc.pxToMm(bottom)

  if (us) {
    left = Calc.mmToIn(left)
    right = Calc.mmToIn(right)
    top = Calc.mmToIn(top)
    bottom = Calc.mmToIn(bottom)
  }

  return (
    <div className="mr-3">
      <div id="sign-size">
        <span className="paragraph-2xs mb-0 font-bold">{t("toolbox.dimensions")}: </span>
        <span className="paragraph-2xs mb-0 lowercase">
          {t("toolbox.width")} {format(width)} {sizeUnit},{" "}
        </span>
        <span className="paragraph-2xs mb-0 lowercase">
          {t("toolbox.height")} {format(height)} {sizeUnit}
        </span>
      </div>

      {angle ? (
        <div id="sign-angle">{Math.floor(objectInfo.angle)}°</div>
      ) : (
        <div id="sign-position" className="text-nowrap">
          <span className="paragraph-2xs mb-0 font-bold">{t("toolbox.position")}: </span>
          <span className="paragraph-2xs mb-0 lowercase">
            {t("toolbox.left")} {format(left)} {sizeUnit},{" "}
          </span>
          <span className="paragraph-2xs mb-0 lowercase">
            {t("toolbox.right")} {format(right)} {sizeUnit},{" "}
          </span>
          <span className="paragraph-2xs mb-0 lowercase">
            {t("toolbox.top")} {format(top)} {sizeUnit},{" "}
          </span>
          <span className="paragraph-2xs mb-0 lowercase">
            {t("toolbox.bottom")} {format(bottom)} {sizeUnit}
          </span>
        </div>
      )}
    </div>
  )
}

export default ObjectInfo
